import React from "react";
import { Tooltip, Tag, message } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import AppTable from "components/AppTable/AppTable";
import { dateTimeFormat } from "constant/constant";
import { messageStatus } from "constant/commonList";
import { getEffectiveScheduleStatus } from "common/utils/scheduleStatus";
import { toJS } from "mobx";

const DataTableSection = ({ loading, dataSource, current, total, pageSize, tabname, onPageChange, onPageSizeChange, onRowClick, onEditSchedule, userTimeZone }) => {
  const formatDateWithTimezone = (date, timezone) => {
    if (!date) return "";

    // Use the selected timezone from dashboard, fallback to UTC
    const selectedTz = timezone || userTimeZone || "UTC";

    try {
      return moment
        .utc(date)
        .tz(selectedTz)
        .format(dateTimeFormat);
    } catch (error) {
      console.error("Error formatting date:", error);
      return moment(date).format(dateTimeFormat);
    }
  };

  // Get the current queue detail based on nextScheduledRun
  const getCurrentQueueDetail = record => {
    if (!record.nextScheduledRun || !record.queueDetail?.length) return null;

    const nextRunDate = moment.utc(record.nextScheduledRun).valueOf();

    return record.queueDetail.find(q => {
      const occurrenceDate = moment.utc(q.occurrenceDate).valueOf();
      return occurrenceDate === nextRunDate;
    });
  };

  const getScheduleTypeInfo = record => {
    const getEndInfo = rec => {
      if (rec?.end_date) {
        return `until ${formatDateWithTimezone(rec.end_date, userTimeZone)}`;
      }
      return "no end date";
    };

    const getQueueStatus = rec => {
      if (!rec.queueDetail?.length) return null;

      const data = record.queueDetail.reduce(
        (acc, q) => {
          if (q.queuestatus === "complete") {
            acc.completed++;
          } else if (q.queuestatus === "scheduled") {
            acc.scheduled++;
          } else if (q.queuestatus === "failed") {
            acc.failed++;
          } else if (q.queuestatus === "cancel") {
            acc.cancelled++;
          }
          return acc;
        },
        { completed: 0, scheduled: 0, failed: 0, cancelled: 0 }
      );

      return data;
    };

    if (!record.repeat || record.repeat === "one_time") {
      return {
        icon: "calendar-day",
        text: "One Time",
        description: `Scheduled for ${formatDateWithTimezone(record?.start_date || record?.scheduleAt, userTimeZone)}`,
        endInfo: null,
        queueInfo: null,
        nextOccurrence: null,
        executedAt: record.executedAt ? formatDateWithTimezone(record.executedAt, userTimeZone) : null
      };
    }

    switch (record.repeat) {
      case "daily":
        return {
          icon: "calendar-day",
          text: "Daily",
          description: `${record.repeatInterval === 1 ? "Every day" : `Every ${record.repeatInterval || 1} days`} starting at ${formatDateWithTimezone(record.start_date, userTimeZone)}`,
          endInfo: getEndInfo(record),
          queueInfo: getQueueStatus(record),
          nextOccurrence: record.nextScheduledRun // Use backend value
        };
      case "weekly":
        return {
          icon: "calendar-week",
          text: "Weekly",
          description: `Every ${record.repeatInterval || 1} week(s)${
            record.daysOfWeek ? ` on ${record.daysOfWeek.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(", ")} starting at ${formatDateWithTimezone(record.start_date, userTimeZone)}` : ""
          }`,
          endInfo: getEndInfo(record),
          queueInfo: getQueueStatus(record),
          nextOccurrence: record.nextScheduledRun // Use backend value
        };
      case "monthly":
        return {
          icon: "calendar-alt",
          text: "Monthly",
          description: `Every ${record?.repeatInterval || 1} month(s)${
            record.dayOfMonth
              ? ` on day ${record.dayOfMonth} starting at ${formatDateWithTimezone(record.start_date, userTimeZone)}`
              : record.weekDayOfMonth
              ? ` on ${record?.weekDayOfMonth?.split(":")?.join(" ")} starting at ${formatDateWithTimezone(record.start_date, userTimeZone)}`
              : ""
          }`,
          endInfo: getEndInfo(record),
          queueInfo: getQueueStatus(record),
          nextOccurrence: record.nextScheduledRun // Use backend value
        };
      default:
        return {
          icon: "calendar",
          text: record.repeat,
          description: "",
          endInfo: getEndInfo(record),
          queueInfo: getQueueStatus(record),
          nextOccurrence: record.nextScheduledRun // Use backend value
        };
    }
  };

  const handleCopyEmail = (email, e) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(email)
      .then(() => {
        message.success("Email copied to clipboard!");
      })
      .catch(() => {
        message.error("Failed to copy email");
      });
  };

  const scheduledColumns = [
    {
      title: "User",
      dataIndex: "user",
      ellipsis: true,
      width: 200,
      render: (user, record) => (
        <div className="d-flex flex-column">
          <span className="fw-semibold">{user?.name || "-"}</span>
          <div className="d-flex align-items-center">
            <small className="text-muted">{user?.email || ""}</small>
            {user?.email && (
              <Tooltip title="Copy Email">
                <CopyOutlined className="ml-2 text-info-color" style={{ cursor: "pointer", fontSize: "12px" }} onClick={e => handleCopyEmail(user.email, e)} />
              </Tooltip>
            )}
          </div>
        </div>
      )
    },
    {
      title: "Campaign Name",
      dataIndex: "name",
      ellipsis: true,
      width: 200,
      render: (text, record) => (
        <span
          role="button"
          onClick={e => {
            e.stopPropagation();
            onRowClick("view", record);
          }}
        >
          {text}
        </span>
      )
    },
    {
      title: "Recipients",
      dataIndex: "recipients",
      ellipsis: true,
      align: "center",
      width: 120
    },
    {
      title: "Schedule Details",
      dataIndex: "scheduleDetails",
      ellipsis: true,
      width: 300,
      render: (text, record) => {
        const currentQueueDetail = getCurrentQueueDetail(record);
        const currentQueueStatus = currentQueueDetail?.queuestatus;
        const isActive = record.status === "running" && currentQueueStatus !== "cancel";
        const scheduleInfo = getScheduleTypeInfo(record);

        return (
          <div className="d-flex flex-column" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
            <Tooltip title={scheduleInfo.description}>
              <div className="d-flex align-items-center mb-2">
                <i className={`fas fa-${scheduleInfo.icon} text-info-color`} />
                <span className="ml-2">
                  Type:
                  <Tag color="blue" className="ml-2">
                    {scheduleInfo.text}
                    {isActive && <span className="schedule-active-dot ml-1" />}
                  </Tag>
                </span>
              </div>
            </Tooltip>

            <Tooltip title={scheduleInfo.description}>
              <div className="d-flex align-items-start mb-2">
                <i className="fas fa-clock text-info-color mt-1" />
                <span className="ml-2" style={{ flex: 1, lineHeight: "1.5" }}>
                  {scheduleInfo?.description}
                </span>
              </div>
            </Tooltip>

            {/* Show executed time for one-time campaigns if available */}
            {scheduleInfo.executedAt && (
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-check-circle text-success" style={{ fontSize: "12px" }} />
                <small className="text-success ml-1">Executed at {scheduleInfo.executedAt}</small>
              </div>
            )}

            {scheduleInfo.endInfo && (
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-flag-checkered text-muted" style={{ fontSize: "12px" }} />
                <small className="text-muted ml-1">{scheduleInfo.endInfo}</small>
              </div>
            )}

            {scheduleInfo.queueInfo && (
              <>
                {currentQueueStatus === "cancel" && record.status === "cancel" ? (
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-ban text-danger" style={{ fontSize: "12px" }} />
                    <small className="text-danger ml-1">Scheduled is cancelled</small>
                  </div>
                ) : (
                  <>
                    {scheduleInfo.nextOccurrence && (
                      <div className="d-flex align-items-center mb-1">
                        <i className="fas fa-calendar-day text-info-color" style={{ fontSize: "12px" }} />
                        <small className="text-muted ml-1">Next: {formatDateWithTimezone(scheduleInfo.nextOccurrence, userTimeZone)}</small>
                      </div>
                    )}
                    <div className="d-flex align-items-center">
                      <i className="fas fa-sync-alt text-info-color" style={{ fontSize: "12px" }} />
                      <small className="text-muted ml-1">
                        {scheduleInfo.queueInfo.completed} sent, {scheduleInfo.queueInfo.scheduled} scheduled
                        {scheduleInfo.queueInfo.cancelled > 0 && `, ${scheduleInfo.queueInfo.cancelled} cancelled`}
                      </small>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Display selected timezone indicator */}
            {userTimeZone && (
              <div className="d-flex align-items-center mt-2">
                <i className="fas fa-globe text-muted" style={{ fontSize: "10px" }} />
                <small className="text-muted ml-1" style={{ fontSize: "10px" }}>
                  {userTimeZone}
                </small>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      className: "text-capitalize",
      align: "center",
      width: 150,
      render: (text, record) => {
        // Get current queue detail status
        const currentQueueDetail = getCurrentQueueDetail(record);
        const currentQueueStatus = currentQueueDetail?.queuestatus;
        const effectiveParentStatus = getEffectiveScheduleStatus(record);

        // Determine which status to display
        let displayStatus = effectiveParentStatus;
        let displayLabel = effectiveParentStatus;

        if (currentQueueStatus) {
          if (currentQueueStatus === "cancel" && record.status === "scheduled") {
            displayStatus = "cancel";
            displayLabel = "Next Cancelled";
          } else if (currentQueueStatus === "cancel" && record.status === "cancel") {
            displayStatus = "cancel";
            displayLabel = "Cancelled";
          } else if (currentQueueStatus === "complete") {
            displayStatus = "complete";
            displayLabel = "Completed";
          } else if (currentQueueStatus === "failed") {
            displayStatus = "failed";
            displayLabel = "Failed";
          } else if (currentQueueStatus === "scheduled") {
            displayStatus = effectiveParentStatus;
            displayLabel = effectiveParentStatus ? messageStatus.find(el => el.value === effectiveParentStatus)?.label : "Scheduled";
          }
        }

        const statusConfig = messageStatus.find(el => el.value === displayStatus);

        return <Tag color={statusConfig?.color || "default"}>{currentQueueStatus === "cancel" ? displayLabel : statusConfig?.label || displayLabel || "Scheduled"}</Tag>;
      }
    }
  ];

  return (
    <AppTable
      loading={loading}
      pagination={{
        defaultCurrent: 1,
        current: current,
        total: total,
        pageSize: pageSize,
        onChange: cur => {
          onPageChange(cur);
        },
        showSizeChanger: true,
        onShowSizeChange: (c, ps) => onPageSizeChange(ps),
        hideOnSinglePage: false,
        showQuickJumper: true
      }}
      columns={scheduledColumns}
      dataSource={dataSource}
      scroll={{ x: 1000, y: 625 }}
      secondary
      rowKey={record => record._id}
      onRow={record => ({
        onClick: () => onRowClick("view", record)
      })}
    />
  );
};

export default DataTableSection;
